import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { useTagsFromBulkContactRequest } from '../../../../../hooks/useTagsFromBulkContactRequest';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { Button } from '../../../../components-v2/Button';
import { Checkbox } from '../../../../components-v2/Checkbox';
import { Databoard, DataboardBody } from '../../../../components/Databoard';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../../components/DataGrid';
import { EntityTablePlaceholder } from '../../../../components/entities/EntityTablePlaceholder';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/Popover';
import { DisclosureIcon } from '../../../../components/svgs/icons/DisclosureIcon';
import { toSentenceCase } from '../../../../utils/toSentenceCase';
import { useTableColumnVisibilityContext } from '../context/TableColumnVisibilityContext';
import { useContactSortPreferences } from '../hooks/useContactSortPreferences';
import { ColumnId } from '../hooks/useTableColumnVisibility';
import { DefaultPeopleFilter, DefaultPeopleSort, usePeopleSearch } from '../PeopleContext';
import { PeopleRow } from '../PeopleRow';
import { styleSheet } from './styles';

interface PeopleTableProps {
	onClearFilters?: () => void;
	onSortChanged?: (newSort: Api.ISortDescriptor<string>) => void;
}

interface ColumnMetadata {
	id: ColumnId;
	sortProperty: string;
}

const sortableColumns = {
	creationDate: {
		id: 'creationDate',
		sortProperty: 'creationDate',
	},
	lastCommunicationDate: {
		id: 'lastCommunicationDate',
		sortProperty: 'communicationMetadata.lastCommunicationDate',
	},
} as Record<ColumnId, ColumnMetadata>;

const isAllTextSearchFilterCriteria = (criteria: Api.IContactFilterCriteria): boolean => {
	if (criteria) {
		if (!!criteria.criteria && criteria.criteria.length > 0) {
			return !!criteria.criteria.find(x => !!isAllTextSearchFilterCriteria(x));
		} else if (criteria.property === Api.ContactFilterCriteriaProperty.All && criteria.value) {
			return true;
		}
	}

	return false;
};

export const PeopleTable: React.FC<PeopleTableProps> = observer(({ onClearFilters, onSortChanged }) => {
	const {
		contacts,
		filter,
		setFilter,
		sort,
		setSort,
		contactsQuery,
		selectionState,
		selectedContactIds,
		excludedContactIds,
		setSelectedContactIds,
		setExcludedContactIds,
		setSelectionState,
	} = usePeopleSearch();
	const { visibleColumns } = useTableColumnVisibilityContext();
	const tagsToHighlight = useTagsFromBulkContactRequest(filter);
	const [lastSelectedContactId, setLastSelectedContactId] = React.useState<string | null>(null);

	// Add contact sort preferences for name-preserving clear functionality
	const userSession = useUserSession();
	const { isNameSort, loadUserSortPreference } = useContactSortPreferences({
		userSession,
		currentSort: sort,
		contactsQueryParams: null,
		storedContactsQuery: null,
	});

	const loadMore = () => {
		if (contactsQuery.hasNextPage) {
			contactsQuery.fetchNextPage();
		}
	};

	const onClearButtonClicked = () => {
		// Use the passed clear function if available (for advanced filters), otherwise fall back to basic clear
		if (onClearFilters) {
			onClearFilters();
		} else {
			setFilter(DefaultPeopleFilter);
			// Preserve name sorting but reset creation date sorting to default
			// Also fallback to latest user preference if current sort is not a name sort
			const sortToUse = isNameSort(sort) ? sort : loadUserSortPreference() || DefaultPeopleSort;
			setSort(sortToUse);
			onSortChanged?.(sortToUse);
		}
	};

	const onToggleContactSelection = (contact: Api.IContact, shiftKey?: boolean) => {
		const contactId = contact.id;
		const currentSelectedIndex = selectedContactIds.indexOf(contactId);
		const nextSelectedContactIds = [...selectedContactIds];

		const allIsSelected = selectionState === Api.ESelectionState.All;
		const someIsSelected = selectionState === Api.ESelectionState.Some;
		const hasExcludedContacts = excludedContactIds.length > 0;

		// If all or some is selected and there are excluded contacts, we need to toggle the excluded contacts, not the selected contacts
		if (allIsSelected || (someIsSelected && hasExcludedContacts)) {
			const nextExcludedContactIds = [...excludedContactIds];
			const currentExcludedIndex = excludedContactIds.indexOf(contactId);

			const isNotExcludedYet = currentExcludedIndex === -1;
			if (isNotExcludedYet) {
				nextExcludedContactIds.push(contactId);
				const hasUnselectedAllContacts = nextExcludedContactIds.length === contacts.length;
				if (hasUnselectedAllContacts) {
					setSelectionState(Api.ESelectionState.None);
				} else {
					setSelectionState(Api.ESelectionState.Some);
				}
			} else {
				nextExcludedContactIds.splice(excludedContactIds.indexOf(contactId), 1);
				const hasRemovedAllExcludedContacts = nextExcludedContactIds.length === 0;
				if (hasRemovedAllExcludedContacts) {
					// Go back to all selected, since some + excluded = all selected minus some
					setSelectionState(Api.ESelectionState.All);
				} else {
					setSelectionState(Api.ESelectionState.Some);
				}
			}
			setExcludedContactIds(nextExcludedContactIds);
			return;
		}

		if (shiftKey && lastSelectedContactId && lastSelectedContactId !== contactId) {
			selectContactRange(lastSelectedContactId, contactId);
			setLastSelectedContactId(contactId);
			return;
		}

		if (currentSelectedIndex > -1) {
			nextSelectedContactIds.splice(currentSelectedIndex, 1);
			setLastSelectedContactId(null);
		} else {
			nextSelectedContactIds.push(contactId);
			setLastSelectedContactId(contactId);
		}
		const isLastSelectedContact = currentSelectedIndex > -1 && selectedContactIds.length === 1;
		if (isLastSelectedContact) {
			setSelectionState(Api.ESelectionState.None);
		} else if (nextSelectedContactIds.length === contacts.length) {
			setSelectionState(Api.ESelectionState.All);
		} else {
			setSelectionState(Api.ESelectionState.Some);
		}
		setSelectedContactIds(nextSelectedContactIds);
	};

	const onSelectAllCheckedChanged = (checked: boolean) => {
		if (checked || selectionState === Api.ESelectionState.Some) {
			setSelectionState(Api.ESelectionState.All);
			setExcludedContactIds([]);
			setSelectedContactIds([]);
		} else {
			setSelectionState(Api.ESelectionState.None);
			setExcludedContactIds([]);
			setSelectedContactIds([]);
			setLastSelectedContactId(null);
		}
	};

	const onSortByName = (newSort: Api.ISortDescriptor) => () => {
		setSort(newSort);
		onSortChanged?.(newSort);
	};

	const onSort = (column: ColumnMetadata) => () => {
		if (column.sortProperty === sort.sortBy && sort.sort === 'desc') {
			const defaultSort = loadUserSortPreference() || DefaultPeopleSort;
			setSort(defaultSort);
			onSortChanged?.(defaultSort);
			return;
		}

		const newSort = {
			sort: sort.sortBy !== column.sortProperty || sort.sort === 'desc' ? 'asc' : 'desc',
			sortBy: column.sortProperty,
		} as Api.ISortDescriptor;
		setSort(newSort);
		onSortChanged?.(newSort);
	};

	const sortDirectionDesc = sort.sort === 'desc';

	const getNameHeader = () => {
		const sortByStringValue = sort.sortBy;
		switch (sortByStringValue) {
			case 'firstName':
				return `Name (First, ${toSentenceCase(sort.sort)}.)`;
			case 'lastName':
				return `Name (Last, ${toSentenceCase(sort.sort)}.)`;
			default:
				return 'Name';
		}
	};

	const getSortColumnHeader = (label: string, metadata: ColumnMetadata) => {
		return sort.sortBy === metadata.sortProperty ? `${label} (${toSentenceCase(sort.sort)}.)` : label;
	};

	const isContactSelected = (contactId: string) => {
		if (selectionState === Api.ESelectionState.All) return true;
		if (selectionState === Api.ESelectionState.Some) {
			if (selectedContactIds.length === 0) {
				return !excludedContactIds.includes(contactId);
			}
			return selectedContactIds.includes(contactId);
		}
		return selectedContactIds.includes(contactId);
	};

	const selectContactRange = (startId: string, endId: string) => {
		const allContacts: Api.IContact[] = [];
		contacts.forEach(contact => {
			allContacts.push(contact);
			if (contact.duplicateContacts?.length) {
				allContacts.push(...contact.duplicateContacts);
			}
		});

		const startIndex = allContacts.findIndex(c => c.id === startId);
		const endIndex = allContacts.findIndex(c => c.id === endId);

		if (startIndex === -1 || endIndex === -1) {
			return;
		}

		const minIndex = Math.min(startIndex, endIndex);
		const maxIndex = Math.max(startIndex, endIndex);

		for (let i = minIndex; i <= maxIndex; i++) {
			const contactToSelect = allContacts[i];
			if (!isContactSelected(contactToSelect.id)) {
				if (selectionState === Api.ESelectionState.All) {
					excludedContactIds.splice(excludedContactIds.indexOf(contactToSelect.id), 1);
				} else {
					selectedContactIds.push(contactToSelect.id);
				}
			}
		}
	};

	return (
		<Databoard styles={[styleSheet.databoard]}>
			<DataboardBody data-scroll-restoration-id='people-table'>
				<DataGrid styles={[styleSheet.dataGrid]}>
					<DataGridColumns>
						{visibleColumns.map(column => {
							const sortMetadata = sortableColumns[column.id];
							if (column.id === 'name') {
								return (
									<DataGridColumn key={column.id} styles={[styleSheet.columnHeader]} sticky>
										<span className={css(styleSheet.nameColumnContent)}>
											<Checkbox
												id='select-all-contacts'
												checked={
													selectionState === Api.ESelectionState.Some
														? 'indeterminate'
														: selectionState === Api.ESelectionState.All
												}
												onCheckedChange={onSelectAllCheckedChanged}
												size='lg'
											/>
											<Popover>
												<PopoverTrigger asChild>
													<div className={css(styleSheet.sortTrigger)}>
														{getNameHeader()}
														<span
															className={css(
																styleSheet.sortIcon,
																(!isNameSort(sort) || !sortDirectionDesc) && styleSheet.sortIconAscending
															)}
														>
															<DisclosureIcon type='chevron' />
														</span>
													</div>
												</PopoverTrigger>
												<PopoverContent align='start' styles={[styleSheet.sortMenu]} sideOffset={4}>
													<>
														<div
															className={css(styleSheet.sortMenuItem)}
															onClick={onSortByName({
																sort: 'asc',
																sortBy: 'firstName',
															})}
														>
															First Name A-Z
														</div>
														<div
															className={css(styleSheet.sortMenuItem)}
															onClick={onSortByName({
																sort: 'desc',
																sortBy: 'firstName',
															})}
														>
															First Name Z-A
														</div>
														<div
															className={css(styleSheet.sortMenuItem)}
															onClick={onSortByName({
																sort: 'asc',
																sortBy: 'lastName',
															})}
														>
															Last Name A-Z
														</div>
														<div
															className={css(styleSheet.sortMenuItem)}
															onClick={onSortByName({
																sort: 'desc',
																sortBy: 'lastName',
															})}
														>
															Last Name Z-A
														</div>
													</>
												</PopoverContent>
											</Popover>
										</span>
									</DataGridColumn>
								);
							} else if (sortMetadata && !isAllTextSearchFilterCriteria(filter?.filter)) {
								return (
									<DataGridColumn key={column.id} styles={[styleSheet.columnHeader]} sticky>
										<Button variant='link' styles={[styleSheet.sortTrigger]} onClick={onSort(sortMetadata)}>
											<span>{getSortColumnHeader(column.label, sortMetadata)}</span>
											{sort.sortBy === sortMetadata.sortProperty && (
												<span className={css(styleSheet.sortIcon, !sortDirectionDesc && styleSheet.sortIconAscending)}>
													<DisclosureIcon type='chevron' />
												</span>
											)}
										</Button>
									</DataGridColumn>
								);
							} else {
								return (
									<DataGridColumn key={column.id} styles={[styleSheet.columnHeader]} sticky>
										{column.label}
									</DataGridColumn>
								);
							}
						})}
					</DataGridColumns>
					{contactsQuery.isPending && !contacts.length ? (
						<DataGridRows>
							<DataGridRow>
								<DataGridRowItem colSpan={visibleColumns.length}>
									<div className={css(styleSheet.loader)}>
										<LoadingSpinner type='large' />
									</div>
								</DataGridRowItem>
							</DataGridRow>
						</DataGridRows>
					) : (
						<DataGridRows>
							{contacts.map(contact => {
								const isSelected = isContactSelected(contact.id);
								if (contact.duplicateContacts?.length) {
									return (
										<>
											<PeopleRow
												isSelected={isSelected}
												onToggleSelection={shiftKey => onToggleContactSelection(contact, shiftKey)}
												contact={contact}
												key={`people-row-${contact.id}`}
												tagsToHighlight={tagsToHighlight}
												visibleColumns={visibleColumns}
											/>
											{contact.duplicateContacts.map(duplicateContact => {
												const duplicateIsSelected = isContactSelected(duplicateContact.id!);
												return (
													<PeopleRow
														isSelected={duplicateIsSelected}
														onToggleSelection={shiftKey => onToggleContactSelection(duplicateContact, shiftKey)}
														contact={duplicateContact}
														key={`people-row-${duplicateContact.id}`}
														tagsToHighlight={tagsToHighlight}
														visibleColumns={visibleColumns}
													/>
												);
											})}
										</>
									);
								}
								return (
									<PeopleRow
										isSelected={isSelected}
										onToggleSelection={shiftKey => onToggleContactSelection(contact, shiftKey)}
										contact={contact}
										key={`people-row-${contact.id}`}
										tagsToHighlight={tagsToHighlight}
										visibleColumns={visibleColumns}
									/>
								);
							})}
						</DataGridRows>
					)}
					{contactsQuery.hasNextPage && !contactsQuery.isFetchingNextPage && (
						<Waypoint key='load-more-trigger' bottomOffset='-400px' onEnter={loadMore} />
					)}
				</DataGrid>
				{contactsQuery.isFetchingNextPage ? <LoadingSpinner type='small' /> : null}
				{!contactsQuery.isPending && !contacts.length ? (
					<EntityTablePlaceholder onClearButtonClicked={onClearButtonClicked} type='contact' />
				) : null}
			</DataboardBody>
		</Databoard>
	);
});
